/**
 * Google Maps Places API Utility
 * Handles dynamic loading of the new Google Maps Places library
 */

class GoogleMapsLoader {
  constructor() {
    this.placesLibrary = null;
    this.autocompleteService = null;
    this.isLoading = false;
    this.loadPromise = null;
    this.scriptLoaded = false;
    this.scriptLoading = false;
  }

  /**
   * Load the Google Maps Places library dynamically
   * @returns {Promise<Object>} Places library object
   */
  async loadPlacesLibrary() {
    if (this.placesLibrary) {
      return this.placesLibrary;
    }

    if (this.isLoading) {
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = this._loadLibrary();
    
    try {
      this.placesLibrary = await this.loadPromise;
      return this.placesLibrary;
    } finally {
      this.isLoading = false;
    }
  }

  async _loadLibrary() {
    try {
      // Ensure Google Maps script is loaded first
      await this._ensureGoogleMapsScript();
      
      // Wait for Google Maps to be available
      await this._waitForGoogleMaps();
      
      // Import the places library using the new API
      const { Place, AutocompleteService, PlacesService } = await google.maps.importLibrary("places");
      
      const library = {
        Place,
        AutocompleteService,
        PlacesService
      };
      
      // Validate that the library loaded correctly
      if (!library.AutocompleteService || !library.Place) {
        throw new Error('Google Places library components not properly loaded');
      }
      
      return library;
    } catch (error) {
      console.error('Failed to load Google Maps Places library:', error);
      throw error;
    }
  }

  /**
   * Ensure Google Maps script is loaded
   */
  async _ensureGoogleMapsScript() {
    if (this.scriptLoaded || typeof google !== 'undefined') {
      return;
    }

    if (this.scriptLoading) {
      // Wait for existing load to complete
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.scriptLoaded || typeof google !== 'undefined') {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
      });
    }

    this.scriptLoading = true;

    return new Promise((resolve, reject) => {
      // Get the Google Maps API key from the environment
      let apiKey = null;
      
      // Try different ways to get the API key
      if (window.Laravel && window.Laravel.env && window.Laravel.env.GOOGLE_JAVASCRIPT_API_KEY) {
        apiKey = window.Laravel.env.GOOGLE_JAVASCRIPT_API_KEY;
      } else if (window.store && window.store.state.App.env.GOOGLE_API_KEY) {
        apiKey = window.store.state.App.env.GOOGLE_API_KEY;
      }

      if (!apiKey) {
        console.warn('Google Maps API key not found, trying to proceed without it');
      }

      // Create the script element
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js${apiKey ? `?key=${apiKey}` : ''}&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;

      // Set up global callback
      window.initGoogleMaps = () => {
        this.scriptLoaded = true;
        this.scriptLoading = false;
        resolve();
      };

      script.onerror = () => {
        this.scriptLoading = false;
        reject(new Error('Failed to load Google Maps script'));
      };

      // Add to document
      document.head.appendChild(script);
    });
  }

  /**
   * Wait for Google Maps API to be loaded
   */
  _waitForGoogleMaps() {
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && google.maps && google.maps.importLibrary) {
        resolve();
        return;
      }

      // Poll for Google Maps availability
      const checkInterval = setInterval(() => {
        if (typeof google !== 'undefined' && google.maps && google.maps.importLibrary) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('Google Maps API failed to load within 10 seconds. Please check if Google Maps is properly loaded.'));
      }, 10000);
    });
  }

  /**
   * Get AutocompleteService instance (new API)
   */
  async getAutocompleteService() {
    if (this.autocompleteService) {
      return this.autocompleteService;
    }

    const library = await this.loadPlacesLibrary();
    this.autocompleteService = new library.AutocompleteService();
    return this.autocompleteService;
  }

  /**
   * Get place predictions using the new API
   * @param {string} input - The input string
   * @param {Object} options - Additional options (types, etc.)
   * @returns {Promise<Array>} Array of place predictions
   */
  async getPlacePredictions(input, options = {}) {
    try {
      // Try the new API approach first
      const library = await this.loadPlacesLibrary();
      const service = new library.AutocompleteService();
      
      const request = {
        input,
        ...options
      };

      // Check if the new API supports promises
      const result = service.getPlacePredictions(request);
      
      if (result && typeof result.then === 'function') {
        // Promise-based new API
        const response = await result;
        if (response && response.predictions) {
          return response.predictions;
        } else if (Array.isArray(response)) {
          return response;
        }
        return [];
      } else {
        // Fallback to callback-based approach
        return new Promise((resolve, reject) => {
          service.getPlacePredictions(request, (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              resolve(predictions);
            } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              resolve([]);
            } else {
              console.warn('Places API returned status:', status);
              resolve([]);
            }
          });
        });
      }
    } catch (error) {
      console.error('Error getting place predictions:', error);
      // Fallback to legacy approach if new API fails completely
      try {
        const service = new google.maps.places.AutocompleteService();
        return new Promise((resolve, reject) => {
          service.getPlacePredictions({
            input,
            ...options
          }, (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              resolve(predictions);
            } else {
              resolve([]);
            }
          });
        });
      } catch (fallbackError) {
        console.error('Fallback prediction API also failed:', fallbackError);
        return [];
      }
    }
  }

  /**
   * Get place details using the new Places API
   * @param {string} placeId - The place ID
   * @param {Array} fields - Array of fields to fetch
   * @returns {Promise<Object>} Place details
   */
  async getPlaceDetails(placeId, fields = ['displayName', 'location', 'addressComponents']) {
    try {
      // Try the new API first
      const library = await this.loadPlacesLibrary();
      
      if (library.Place) {
        // Create a Place instance with the place ID
        const place = new library.Place({ id: placeId });
        
        // Fetch the requested fields
        const result = await place.fetchFields({ fields });
        
        // Get the place data from the result
        const newPlaceData = result.place;
        
        // Map new API format to legacy format for backward compatibility
        const placeData = {
          // Map displayName to name
          name: newPlaceData.displayName || newPlaceData.name,
          
          // Map location to geometry.location
          geometry: {
            location: newPlaceData.location ? {
              lat: () => newPlaceData.location.lat(),
              lng: () => newPlaceData.location.lng()
            } : null
          },
          
          // Map addressComponents to address_components
          address_components: newPlaceData.addressComponents || newPlaceData.address_components || [],
          
          // Keep any other existing properties
          ...newPlaceData
        };
        
        // Ensure geometry location has proper methods
        if (placeData.geometry && placeData.geometry.location) {
          // If location doesn't have methods, create them
          if (typeof placeData.geometry.location.lat !== 'function') {
            const lat = placeData.geometry.location.lat;
            const lng = placeData.geometry.location.lng;
            placeData.geometry.location = {
              lat: () => lat,
              lng: () => lng
            };
          }
        }
        
        return placeData;
      } else {
        throw new Error('New Place API not available');
      }
    } catch (error) {
      console.error('New Places API failed, falling back to legacy:', error);
      
      // Fallback to legacy PlacesService
      return new Promise((resolve, reject) => {
        const service = new google.maps.places.PlacesService(document.createElement('div'));
        service.getDetails(
          { 
            placeId: placeId,
            fields: fields 
          }, 
          (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && place) {
              resolve(place);
            } else {
              reject(new Error(`Places service returned status: ${status}`));
            }
          }
        );
      });
    }
  }

  /**
   * Legacy compatibility method for getDetails callback style
   * @param {Object} request - Request object with placeId
   * @param {Function} callback - Callback function
   */
  async getDetails(request, callback) {
    try {
      const place = await this.getPlaceDetails(request.placeId);
      callback(place);
    } catch (error) {
      console.error('Error in legacy getDetails method:', error);
      callback(null);
    }
  }
}

// Create a singleton instance
const googleMapsLoader = new GoogleMapsLoader();

export default googleMapsLoader;